import { useState, useCallback, useEffect } from 'react';
import {
  Map,
  AdvancedMarker,
  Pin,
} from '@vis.gl/react-google-maps';
import { Flex, Spinner, Text } from '@chakra-ui/react';
import { useConfig } from '@src/context/config';

function LocationMapSelector({ onLocationChange }) {
  const [userLocation, setUserLocation] = useState(null);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [error, setError] = useState(null);
  const config = useConfig();

  // Fetch user location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setUserLocation(coords);
          setMarkerPosition(coords); // Set initial marker position to user location
          onLocationChange(coords);
        },
        (err) => {
          console.error('Error retrieving location:', err);
          setError(err);
        },
      );
    } else {
      console.error('Geolocation is not supported by this browser.');
      setError('Geolocation is not supported by this browser.');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle map clicks to update marker position
  const onMapClick = useCallback((event) => {
    const newPosition = {
      lat: event.detail.latLng.lat,
      lng: event.detail.latLng.lng,
    };
    setMarkerPosition(newPosition);
    onLocationChange(newPosition);
  }, [onLocationChange]);

  // Handle marker drag to update position
  const onMarkerDragEnd = useCallback((event) => {
    const newPosition = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng(),
    };
    setMarkerPosition(newPosition);
    onLocationChange(newPosition);
  }, [onLocationChange]);

  if (error) {
    return (
      <Flex justify="center" align="center" p={8} background="red.50" borderRadius="md">
        <Text color="red.500">Error retrieving location. Please allow location access and refresh the page.</Text>
      </Flex>
    );
  }

  return userLocation && !error ? (
    <Map
      defaultCenter={userLocation}
      defaultZoom={15}
      onClick={onMapClick}
      options={{
        disableDefaultUI: true,
        zoomControl: true,
        mapTypeControl: false,
        fullscreenControl: false,
        draggable: true,
        scrollwheel: true,
      }}
      style={{ width: '100%', height: '250px' }}
      mapId={config?.GOOGLE_MAP_LOCATIONS_ID}
    >
      {markerPosition && (
      <AdvancedMarker
        position={markerPosition}
        draggable
        onDragEnd={onMarkerDragEnd}
      >
        <Pin />
      </AdvancedMarker>
      )}
    </Map>
  ) : (
    <Flex justify="center" align="center" h="100px" background="gray.50" borderRadius="md">
      <Spinner />
      <Text ml={4} fontSize="sm">Loading your location</Text>
    </Flex>
  );
}

export default LocationMapSelector;
